import { useMutation } from '@tanstack/react-query'

import { authClient } from '@/lib/auth-client'
import { useEncryptionKey } from '@/store'
import { useApiClient } from '@/trpc/react'
import { encrypt } from '@/utils'

const MESSAGE_VERSION = 2

interface UseSaveMessageArgs {
  messages: {
    content: string
    role: 'user' | 'assistant'
    created_at: string
  }[]
  prevContext: string
}

const useSaveMessages = () => {
  const apiClient = useApiClient()
  const { data: session } = authClient.useSession()
  const user = session?.user
  const encryptionKey = useEncryptionKey()

  return useMutation({
    mutationFn: async ({ messages, prevContext }: UseSaveMessageArgs) => {
      if (!user || !encryptionKey) return
      const userMessage = messages.find((message) => message.role === 'user')
      const assistantMessage = messages.find(
        (message) => message.role === 'assistant',
      )

      if (!userMessage || !assistantMessage) return

      const { embeddings, values } =
        await apiClient.ai.generateManyEmbeddings.query({
          content: [userMessage.content, assistantMessage.content],
        })

      // Find the correct indices by matching content with values
      const userEmbeddingIndex = values.findIndex(
        (value) => value === userMessage.content,
      )
      const assistantEmbeddingIndex = values.findIndex(
        (value) => value === assistantMessage.content,
      )

      // Use the correct indices to get the corresponding embeddings
      const userEmbedding =
        userEmbeddingIndex !== -1
          ? embeddings[userEmbeddingIndex]
          : embeddings[0]
      const assistantEmbedding =
        assistantEmbeddingIndex !== -1
          ? embeddings[assistantEmbeddingIndex]
          : embeddings[1]

      const [encryptedUserMessage, encryptedAssistantMessage] =
        await Promise.all([
          encrypt(userMessage?.content ?? '', encryptionKey),
          encrypt(assistantMessage?.content ?? '', encryptionKey),
        ])

      await apiClient.messages.save.mutate({
        messages: [
          {
            messageEncrypted: encryptedUserMessage,
            userOrigin: 'user',
            messageEmbeddings: userEmbedding,
            version: MESSAGE_VERSION,
            createdAt: userMessage.created_at,
          },
          {
            messageEncrypted: encryptedAssistantMessage,
            userOrigin: 'bot',
            messageEmbeddings: assistantEmbedding,
            version: MESSAGE_VERSION,
            createdAt: assistantMessage.created_at,
          },
        ],
      })

      // Generate, encrypt and update new user context
      const contextInput = `user input: ${userMessage?.content}. almi (AI assistant) response: ${assistantMessage?.content}`
      const newUserContext = await apiClient.ai.generateUserContext.query({
        userContext: prevContext,
        userInput: contextInput,
      })
      const encryptedNewUserContext = await encrypt(
        newUserContext,
        encryptionKey,
      )
      await apiClient.userSettings.update.mutate({
        generalInformation: encryptedNewUserContext,
      })
    },
  })
}

export default useSaveMessages
